import { BaseApiClient, API_BASE_URL } from './base-client';
import type { ApiResponse, Message, StreamingStatus } from '../../types';
import { clearAuthData, getAuthHeaders } from '../../utils/authHeaders';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function sanitizeTeamId(teamId?: string | null): string | null {
  return teamId && UUID_REGEX.test(teamId) ? teamId : null;
}

/**
 * Chat API - методы для работы с чатом и сессиями
 */
export class ChatApi extends BaseApiClient {
  
  // ===== CHAT API (Responses API) =====
  
  /**
   * Отправить сообщение в чат (без streaming)
   */
  async sendChatMessage(params: {
    message: string;
    provider: string;
    model: string;
    conversationHistory: Message[];
    attachments?: File[];
    useNetwork?: boolean;
    reasoningEffort?: string;
    reasoningSummary?: boolean;
    maxOutputTokens?: number;
    codeInterpreter?: boolean;
    deepResearch?: boolean;
    deepResearchMode?: 'fast' | 'thorough';
    verbosity?: 'concise' | 'normal' | 'detailed';
    isEditedMessage?: boolean;
    editedMessageId?: string;
    existingAssistantMessageId?: string;
    sessionId?: string | null;
  }): Promise<ApiResponse<Message>> {
    // Формат согласно OpenAI Responses API
    
    // Подготавливаем content с файлами
    const userContent: any[] = [{ type: 'input_text', text: params.message }];
    
    // Добавляем файлы в content согласно документации
    if (params.attachments && params.attachments.length > 0) {
      // Проверка поддержки vision моделью (для файлов)
      const modelSupportsVision = this.checkModelSupportsVision(params.provider, params.model);
      
      // Если модель не поддерживает vision, просто пропускаем файлы (не отправляем их)
      if (!modelSupportsVision) {
        // Модель не поддерживает файлы
      } else {
        // Проверка размера файлов (согласно документации OpenAI)
        const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200 MB на файл
        const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200 MB всего
        
        let totalSize = 0;
        for (const file of params.attachments) {
          if (file.size > MAX_FILE_SIZE) {
            throw new Error(`Файл "${file.name}" слишком большой. Максимальный размер: 200 MB`);
          }
          totalSize += file.size;
        }
        
        if (totalSize > MAX_TOTAL_SIZE) {
          throw new Error(`Общий размер файлов превышает 200 MB. Текущий: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
        }
        
        const fileContents = await Promise.all(
          params.attachments.map(async (file) => {
            // Конвертируем файл в base64
            const base64 = await this.fileToBase64(file);
            
            // Определяем тип файла
            const mimeType = file.type;
            
            // Для изображений используем input_image
            if (mimeType.startsWith('image/')) {
              return {
                type: 'input_image',
                input_image: {
                  data: base64.split(',')[1], // Убираем data:image/...;base64,
                  format: mimeType.split('/')[1]
                }
              };
            }
            
            // Для PDF и других файлов используем input_file
            return {
              type: 'input_file',
              filename: file.name,
              file_data: base64
            };
          })
        );
        
        userContent.push(...fileContents);
      }
    }
    
    // Очищаем историю от лишних полей (timestamp, id, attachments и т.д.)
    const cleanedHistory = params.conversationHistory.map(msg => ({
      role: msg.role,
      content: typeof msg.content === 'string' 
        ? [{ type: 'input_text', text: msg.content }]
        : msg.content
    }));
    
    const payload = {
      model: params.model,
      provider: params.provider,
      input: [
        ...cleanedHistory,
        {
          role: 'user' as const,
          content: userContent
        }
      ],
      // Web search через tools согласно документации
      ...(params.useNetwork && { 
        tools: [{ type: 'web_search' }] 
      }),
      // Code Interpreter для ChatGPT
      ...(params.codeInterpreter && { 
        codeInterpreter: true 
      }),
      // Deep Research для ChatGPT
      ...(params.deepResearch && {
        deepResearch: true,
        ...(params.deepResearchMode && { deepResearchMode: params.deepResearchMode })
      }),
      // Verbosity для GPT-5.1
      ...(params.verbosity && params.verbosity !== 'normal' && { 
        verbosity: params.verbosity 
      }),
      // Reasoning: разная логика для ChatGPT, Gemini и Claude
      ...(params.reasoningEffort && { 
        reasoning: params.provider === 'gemini' || params.provider === 'google'
          ? { 
              // Gemini: простое вкл/выкл (dynamic thinking)
              enabled: true 
            }
          : { 
              // ChatGPT и Claude: уровни effort (low, medium, high)
              effort: params.reasoningEffort,
              // Включаем summary если запрошено (auto = detailed для большинства моделей)
              ...(params.reasoningSummary && { summary: 'auto' })
            }
      }),
      // Ограничение токенов для контроля затрат
      ...(params.maxOutputTokens && { max_output_tokens: params.maxOutputTokens }),
      // Флаги для редактирования сообщений
      ...(params.isEditedMessage && { isEditedMessage: true }),
      ...(params.editedMessageId && { editedMessageId: params.editedMessageId }),
      ...(params.existingAssistantMessageId && { existingAssistantMessageId: params.existingAssistantMessageId })
    };

    const response = await this.client.post('/api/chat', payload);
    
    // Обработка incomplete responses
    const data = response.data;
    if (data.status === 'incomplete') {
      if (data.incomplete_details?.reason === 'max_output_tokens') {
        // Добавляем предупреждение в сообщение
        if (data.message) {
          data.message += '\n\n⚠️ Ответ был обрезан из-за ограничения токенов.';
        }
      }
    }
    
    return data;
  }

  /**
   * Отправить сообщение в чат с streaming (SSE)
   */
  async streamChatMessage(
    params: Parameters<typeof this.sendChatMessage>[0] & { 
      youtubeUrl?: string;
      s3Attachments?: Array<{ file: File; s3Key: string; s3Url: string; fromGallery?: boolean; thumbnailUrl?: string; thumbnailS3Key?: string; trimRange?: { start: number; end: number }; durationSec?: number }>;
      editSessionId?: string;
      teamId?: string; // Team ID для режима команды
      isRegeneration?: boolean; // Флаг регенерации ответа (не создавать новое сообщение пользователя)
      existingUserMessageId?: string; // ID существующего сообщения пользователя для регенерации
      userMessageId?: string; // ID сообщения пользователя от фронтенда
      assistantMessageId?: string; // ID сообщения ассистента от фронтенда
      mentionedAssistants?: string[]; // @mention: известные ассистенты/стили
      freeMentions?: string[]; // @mention: свободные роли
    },
    onChunk: (chunk: string) => void,
    onStatusUpdate?: (status: StreamingStatus) => void
  ): Promise<{ sources?: any[]; searchQueries?: string[]; userMessageId?: string; assistantMessageId?: string; usage?: any; newBalance?: number; reasoningContent?: string }> {
    // Подготавливаем content с файлами для корректной работы с API
    let userContent: any[] = [];
    
    // Извлекаем YouTube URL (поддержка watch, shorts, live, embed, youtu.be)
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|live\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const youtubeMatch = params.message?.match(youtubeRegex);
    const detectedYoutubeUrl = youtubeMatch ? youtubeMatch[0] : null;

    // Для Gemini: YouTube URL уходит как file_data БЕЗ mimeType (wildcard 'video/*' ломает парсинг)
    const isGeminiProvider = params.provider === 'gemini' || params.provider === 'google';
    if (detectedYoutubeUrl && isGeminiProvider) {
      userContent.push({
        type: 'file_data',
        file_uri: detectedYoutubeUrl,
      });
    }
    
    // ВАЖНО: Сначала добавляем текст, потом файлы (так требует OpenAI)
    if (params.message && params.message.trim()) {
      userContent.push({ 
        type: 'input_text', 
        text: params.message.trim() 
      });
    }
    
    // Проверка поддержки vision моделью перед обработкой файлов
    const modelSupportsVision = this.checkModelSupportsVision(params.provider, params.model);
    
    // ОПТИМИЗАЦИЯ: Используем S3 файлы если они уже загружены
    if (params.s3Attachments && params.s3Attachments.length > 0) {
      // Если модель не поддерживает vision, пропускаем файлы
      if (!modelSupportsVision) {
        // Модель не поддерживает файлы
      } else {
        // Файлы уже в S3 - отправляем только метаданные
        const s3FileContents = params.s3Attachments.map(({ file, s3Key, s3Url, fromGallery, thumbnailUrl, thumbnailS3Key, trimRange, durationSec }) => {
          const mimeType = file.type;

          // Для изображений
          if (mimeType.startsWith('image/')) {
            return {
              type: 'input_image',
              s3Key: s3Key,
              s3Url: s3Url,
              image_url: s3Url, // Для совместимости с OpenAI
              mimeType: mimeType,
              filename: file.name,
              ...(fromGallery ? { fromGallery: true } : {})
            };
          }

          // Для видео
          if (mimeType.startsWith('video/')) {
            const hasTrim = trimRange
              && Number.isFinite(trimRange.start)
              && Number.isFinite(trimRange.end)
              && trimRange.end > trimRange.start;
            return {
              type: 'input_video',
              s3Key: s3Key,
              s3Url: s3Url,
              mimeType: mimeType,
              filename: file.name,
              ...(thumbnailUrl ? { thumbnailUrl, thumbnailS3Key } : {}),
              ...(fromGallery ? { fromGallery: true } : {}),
              ...(typeof durationSec === 'number' && Number.isFinite(durationSec) ? { durationSec } : {}),
              ...(hasTrim ? {
                videoMetadata: {
                  startOffset: trimRange!.start,
                  endOffset: trimRange!.end,
                }
              } : {}),
            };
          }

          // Для PDF и других документов
          return {
            type: 'input_file',
            s3Key: s3Key,
            s3Url: s3Url,
            mimeType: mimeType,
            filename: file.name,
            ...(thumbnailUrl ? { thumbnailUrl, thumbnailS3Key } : {}),
            ...(fromGallery ? { fromGallery: true } : {})
          };
        });
        
        userContent.push(...s3FileContents);
      }
    } 
    // FALLBACK: Старая логика с base64 (для обратной совместимости)
    else if (params.attachments && params.attachments.length > 0) {
      // Если модель не поддерживает vision, пропускаем файлы
      if (!modelSupportsVision) {
        // Модель не поддерживает файлы
      } else {
        // Проверка размера файлов с учетом типа
        const MAX_VIDEO_SIZE = 500 * 1024 * 1024; // 500 MB для видео
        const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB для остальных
        const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB всего (если есть видео)
        
        let totalSize = 0;
        let hasVideo = false;
        
        for (const file of params.attachments) {
          const isVideo = file.type.startsWith('video/');
          const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_FILE_SIZE;
          
          if (isVideo) hasVideo = true;
          
          if (file.size > maxSize) {
            const maxSizeMB = Math.floor(maxSize / 1024 / 1024);
            throw new Error(`Файл "${file.name}" слишком большой. Максимальный размер: ${maxSizeMB} MB`);
          }
          totalSize += file.size;
        }
        
        const actualMaxTotal = hasVideo ? MAX_TOTAL_SIZE : MAX_FILE_SIZE;
        if (totalSize > actualMaxTotal) {
          throw new Error(`Общий размер файлов превышает ${Math.floor(actualMaxTotal / 1024 / 1024)} MB. Текущий: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
        }
        
        // Конвертируем файлы в base64
        const fileContents = await Promise.all(
          params.attachments.map(async (file) => {
            const base64 = await this.fileToBase64(file);
            const mimeType = file.type;
            
            // Для изображений используем input_image
            if (mimeType.startsWith('image/')) {
              return {
                type: 'input_image',
                input_image: {
                  data: base64.split(',')[1],
                  format: mimeType.split('/')[1]
                },
                file_data: base64 // Для backend обработки
              };
            }
            
            // Для видео используем специальный маркер для Files API
            if (mimeType.startsWith('video/')) {
              // Видео нужно загружать через Files API на backend
              return {
                type: 'input_video',
                filename: file.name,
                file_data: base64,
                mimeType: mimeType
              };
            }
            
            // Для PDF и других документов используем input_file
            return {
              type: 'input_file',
              filename: file.name,
              file_data: base64
            };
          })
        );
        
        userContent.push(...fileContents);
      }
    }
    
    // Проверяем наличие файлов (учитываем что они могут быть отфильтрованы)
    const hasFiles = modelSupportsVision && (
      (params.s3Attachments && params.s3Attachments.length > 0) || 
      (params.attachments && params.attachments.length > 0)
    );
    
    
    // Формат для нашего backend
    const payload = {
      message: params.message || '',
      provider: params.provider,
      model: params.model,
      conversationHistory: params.conversationHistory,
      webSearch: params.useNetwork || false,
      hasAttachments: hasFiles,
      userContent: userContent.length > 0 ? userContent : undefined,
      youtubeUrl: detectedYoutubeUrl || undefined, // Передаем YouTube URL отдельно
      // Reasoning: разная логика для ChatGPT и Gemini
      // Для ChatGPT - передаем только если включен (с уровнем effort)
      // Для Gemini - всегда передаем enabled: true/false чтобы явно управлять thinking
      ...(
        params.provider === 'gemini' || params.provider === 'google'
          ? {
              // Gemini: всегда передаем enabled (true или false)
              reasoning: { enabled: !!params.reasoningEffort }
            }
          : params.reasoningEffort 
            ? {
                // ChatGPT: передаем effort только если включен
                reasoning: { effort: params.reasoningEffort }
              }
            : {}
      ),
      // Code Interpreter для ChatGPT
      ...(params.codeInterpreter ? { codeInterpreter: true } : {}),
      // Deep Research для ChatGPT
      ...(params.deepResearch ? { deepResearch: true, ...(params.deepResearchMode && { deepResearchMode: params.deepResearchMode }) } : {}),
      // Verbosity для GPT-5.1 (контроль объема ответа)
      // Документация: https://platform.openai.com/docs/guides/latest-model#verbosity
      ...(params.verbosity ? { verbosity: params.verbosity } : {}),
      // Флаги для редактирования сообщений
      ...(params.isEditedMessage && { isEditedMessage: true }),
      ...(params.editedMessageId && { editedMessageId: params.editedMessageId }),
      ...(params.existingAssistantMessageId && { existingAssistantMessageId: params.existingAssistantMessageId }),
      ...(params.editSessionId && { editSessionId: params.editSessionId }),
      // Session ID для привязки к чату
      ...(params.sessionId && { sessionId: params.sessionId }),
      // Team ID для режима команды (списание с командного баланса)
      ...(sanitizeTeamId(params.teamId) && { teamId: sanitizeTeamId(params.teamId) }),
      // Флаги для регенерации ответа (не создавать новое сообщение пользователя)
      ...(params.isRegeneration && { isRegeneration: true }),
      ...(params.existingUserMessageId && { existingUserMessageId: params.existingUserMessageId }),
      // ID сообщений от фронтенда (для синхронизации с БД)
      ...(params.userMessageId && { userMessageId: params.userMessageId }),
      ...(params.assistantMessageId && { assistantMessageId: params.assistantMessageId }),
      // @mention ассистенты и свободные роли
      ...(params.mentionedAssistants?.length ? { mentionedAssistants: params.mentionedAssistants } : {}),
      ...(params.freeMentions?.length ? { freeMentions: params.freeMentions } : {}),
    };

    // Подготавливаем заголовки с Telegram initData и платформой
    const authHeaders = getAuthHeaders();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...authHeaders
    };

    // Добавляем флаг stream для включения SSE
    const streamPayload = {
      ...payload,
      stream: true // Включаем streaming
    };

    // Для Deep Research и GPT Pro моделей используем большой таймаут (45 минут)
    // Для обычных моделей - 15 минут
    const isDeepResearch = params.model?.includes('deep-research') ||
                          ['o3-deep-research', 'o4-mini-deep-research'].includes(params.model || '');
    const isLongThinking = params.model?.includes('gpt-5.4-pro') || params.model?.includes('gpt-5.5-pro');
    const timeoutMs = (isDeepResearch || isLongThinking) ? 45 * 60 * 1000 : 15 * 60 * 1000;
    
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), timeoutMs);

    try {
      const bodyString = JSON.stringify(streamPayload);
      
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers,
        body: bodyString,
        signal: abortController.signal
      });

      if (!response.ok) {
      // Обработка 401 - данные авторизации устарели
      if (response.status === 401) {
        // Проверяем, запущено ли приложение внутри Telegram Mini App
        const webAppData = (window as any).Telegram?.WebApp?.initData;
        const isInMiniApp = typeof webAppData === 'string' && webAppData.length > 0;
        
        if (isInMiniApp) {
          // В Mini App НЕ редиректим на login - OAuth widget не работает в iframe
          console.error('❌ Auth error in Mini App (401)');
          const webApp = (window as any).Telegram?.WebApp;
          if (webApp?.showPopup) {
            webApp.showPopup({
              title: 'Ошибка авторизации',
              message: 'Пожалуйста, закройте и откройте приложение заново.',
              buttons: [{ type: 'close' }]
            });
          }
        } else {
          clearAuthData();
          localStorage.removeItem('user-storage');
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      
      let errorData;
      
      try {
        const responseText = await response.text();
        try {
          errorData = JSON.parse(responseText);
        } catch {
          errorData = { 
            error: 'Сервер вернул HTML вместо JSON. Возможно ошибка nginx.',
            errorType: 'server_error',
            rawResponse: responseText.substring(0, 200)
          };
        }
      } catch {
        errorData = { 
          error: 'Ошибка соединения с сервером',
          errorType: 'network_error' 
        };
      }
      
      // Создаём понятную ошибку на основе типа
      const error = new Error(errorData.error || 'Произошла ошибка при отправке сообщения');

      // Добавляем метаданные для обработки в UI
      (error as any).errorType = errorData.errorType;
      (error as any).errorCode = errorData.errorCode || errorData.code;
      (error as any).statusCode = response.status;
      (error as any).retryable = errorData.retryable;
      (error as any).isRaiBlock = errorData.isRaiBlock;
      (error as any).retryAfter = errorData.retryAfter;

      throw error;
      }

      // ============ РЕАЛЬНЫЙ SSE STREAMING ============
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Streaming not supported');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let sources: any[] = [];
      let searchQueries: string[] = [];
      let userMessageId: string | undefined;
      let assistantMessageId: string | undefined;
      let usage: any = {};
      let newBalance: number | undefined;
      // ✅ Переменные для версий сообщения ассистента (при редактировании)
      let assistantVersions: any[] | undefined;
      let assistantCurrentVersionIndex: number | undefined;
      let assistantEditSessionId: string | undefined;
      // ✅ Накопление reasoning content (Extended Thinking)
      let reasoningContent = '';
      // ✅ Citations (inline url_citation от OpenAI)
      let citations: any[] = [];

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) {
            break;
          }

          // Декодируем chunk и добавляем в буфер
          buffer += decoder.decode(value, { stream: true });
          
          // Разбиваем на строки
          const lines = buffer.split('\n');
          // Последняя строка может быть неполной, оставляем в буфере
          buffer = lines.pop() || '';

          for (const line of lines) {
            // SSE формат: "data: {json}"
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              if (data === '[DONE]') {
                break;
              }

              try {
                const event = JSON.parse(data);
                
                // Обрабатываем разные типы событий
                if (event.type === 'stream.start') {
                  assistantMessageId = event.messageId;
                  // Deep Research: пробрасываем стадию (clarification/research) из бэкенда
                  if (event.deepResearchStage && onStatusUpdate) {
                    onStatusUpdate({
                      type: 'deep_research.stage',
                      status: event.deepResearchStage,
                      message: ''
                    });
                  }
                }
                else if (event.type === 'content.delta') {
                  // Отправляем текстовый чанк в callback
                  onChunk(event.delta);
                }
                else if (event.type === 'status.update') {
                  // Накапливаем thinking delta для Extended Thinking
                  if (event.status === 'thinking' && event.delta) {
                    reasoningContent += event.delta;
                  }
                  // Обновление статуса (web search, function call, thinking, etc)
                  if (onStatusUpdate) {
                    onStatusUpdate(event);
                  }
                }
                // ✅ НОВОЕ: Обработка sources в реальном времени
                else if (event.type === 'sources.update') {
                  // Добавляем новые sources к существующим
                  if (event.sources && Array.isArray(event.sources)) {
                    sources.push(...event.sources);
                    
                    // Также отправляем как status update для UI
                    if (onStatusUpdate) {
                      onStatusUpdate({
                        type: 'sources.update',
                        status: event.status || 'sources_received',
                        message: event.message || `Получено источников: ${sources.length}`,
                        sources: sources // Передаем все накопленные sources
                      } as any);
                    }
                  }
                }
                // ✅ Web Search action details (query, open_page)
                else if (event.type === 'web_search.action') {
                  if (onStatusUpdate) {
                    onStatusUpdate({
                      type: 'web_search.action',
                      status: 'action',
                      message: event.message,
                      action: event.action,
                      query: event.query
                    } as any);
                  }
                }
                // ✅ НОВОЕ: Обработка usage в реальном времени (только накопление данных)
                else if (event.type === 'usage.update') {
                if (event.usage) {
                  usage = event.usage;
                  // Не отправляем в UI - пользователю не нужна информация о токенах
                }
              }
              // ✅ НОВОЕ: Обработка результатов file search
              else if (event.type === 'file_search.results') {
                if (event.results && Array.isArray(event.results)) {
                  // File search results также добавляем к sources
                  sources.push(...event.results);
                  
                  if (onStatusUpdate) {
                    onStatusUpdate({
                      type: 'file_search.results',
                      status: event.status || 'file_search_complete',
                      message: event.message || `Найдено в документах: ${event.results.length}`,
                      sources: sources
                    } as any);
                  }
                }
              }
              // ✅ НОВОЕ: Обработка файлов Code Interpreter
              else if (event.type === 'code_interpreter.files') {
                if (event.files && Array.isArray(event.files)) {
                  if (onStatusUpdate) {
                    onStatusUpdate({
                      type: 'code_interpreter.files',
                      status: event.status || 'files_ready',
                      message: event.message || `Получено файлов: ${event.files.length}`,
                      files: event.files
                    } as any);
                  }
                }
              }
              else if (event.type === 'code.delta') {
                // Отображаем код который пишет AI
                // Можно добавить отдельный callback для кода
              }
              else if (event.type === 'stream.complete') {
                // В конце также обновляем данные (на случай если что-то пропустили)
                if (event.sources && event.sources.length > 0) {
                  // Дополняем sources из финального события (если есть новые)
                  const newSources = event.sources.filter((s: any) => 
                    !sources.some(existing => existing.url === s.url || existing.title === s.title)
                  );
                  if (newSources.length > 0) {
                    sources.push(...newSources);
                  }
                }
                
                // Обновляем attachments из Code Interpreter если есть
                if (event.attachments && Array.isArray(event.attachments) && event.attachments.length > 0) {
                  // Добавляем файлы к сообщению через onStatusUpdate callback
                  if (onStatusUpdate) {
                    // Преобразуем формат attachments из БД в формат для SSE
                    const files = event.attachments.map((att: any) => ({
                      id: att.id,
                      filename: att.filename || att.name,
                      s3Url: att.s3Url || att.url, // Используем s3Url
                      mimeType: att.mimeType,
                      size: att.size
                    }));
                    
                    // Отправляем финальное обновление с файлами
                    onStatusUpdate({
                      type: 'code_interpreter.files',
                      status: 'files_ready',
                      message: `Получено файлов: ${files.length}`,
                      files: files
                    } as any);
                  }
                }
                
                // Обновляем usage если еще не получили
                if (event.usage && Object.keys(event.usage).length > 0) {
                  usage = event.usage;
                }
                
                userMessageId = event.userMessageId;
                if (event.messageId) {
                  assistantMessageId = event.messageId;
                }
                
                // ✅ Сохраняем версии сообщения ассистента (при редактировании)
                if (event.assistantVersions) {
                  assistantVersions = event.assistantVersions;
                  assistantCurrentVersionIndex = event.assistantCurrentVersionIndex;
                  assistantEditSessionId = event.assistantEditSessionId;
                }

                // ✅ Reasoning content из stream.complete (финальный, полный)
                if (event.reasoningContent) {
                  reasoningContent = event.reasoningContent;
                }

                // ✅ Citations (inline url_citation)
                if (event.citations && event.citations.length > 0) {
                  citations = event.citations;
                }

                // Актуальный баланс токенов после списания
                if (event.newBalance !== undefined) {
                  newBalance = event.newBalance;
                }
              }
              else if (event.type === 'stream.error') {
                const streamErr = new Error(event.error || 'Произошла ошибка. Попробуйте ещё раз.');
                (streamErr as any).errorType = event.errorType;
                (streamErr as any).retryable = event.retryable;
                (streamErr as any).messageId = event.messageId;
                throw streamErr;
              }
              // ✅ НОВОЕ: Обработка Deep Research событий
              else if (event.type === 'deep_research.start') {
                if (onStatusUpdate) {
                  onStatusUpdate({
                    type: 'deep_research.start',
                    status: 'starting',
                    message: event.message || 'Запуск глубокого исследования...'
                  });
                }
                assistantMessageId = event.messageId;
              }
              else if (event.type === 'deep_research.progress') {
                if (onStatusUpdate) {
                  onStatusUpdate({
                    type: 'deep_research.progress',
                    status: event.status || 'in_progress',
                    message: event.message,
                    elapsed: event.elapsed,
                    attempts: event.attempts,
                    maxAttempts: event.maxAttempts
                  });
                }
              }
              else if (event.type === 'deep_research.action') {
                if (onStatusUpdate) {
                  onStatusUpdate({
                    type: 'deep_research.action',
                    status: 'action',
                    message: event.message,
                    action: event.action,
                    toolCallsCount: event.toolCallsCount
                  });
                }
              }
              else if (event.type === 'deep_research.complete') {
                if (onStatusUpdate) {
                  onStatusUpdate({
                    type: 'deep_research.complete',
                    status: 'completed',
                    message: event.message || 'Исследование завершено'
                  });
                }
              }
              // ✅ Обработка автоматического переименования чата
              else if (event.type === 'session.renamed') {
                if (onStatusUpdate) {
                  onStatusUpdate({
                    type: 'session.renamed',
                    status: 'renamed',
                    message: event.newName,
                    sessionId: event.sessionId,
                    newName: event.newName
                  } as any);
                }
              }
                else if (event.type === 'message.complete') {
                  // Deep Research финальный результат
                  if (event.content) {
                    onChunk(event.content);
                  }
                  if (event.sources && event.sources.length > 0) {
                    sources.push(...event.sources);
                    
                    // Отправляем sources в UI для отображения
                    if (onStatusUpdate) {
                      onStatusUpdate({
                        type: 'sources.update',
                        status: 'sources_received',
                        message: `Найдено источников: ${event.sources.length}`,
                        sources: sources
                      } as any);
                    }
                  }
                  if (event.messageId) {
                    assistantMessageId = event.messageId;
                  }
                  // Citations (inline url_citation)
                  if (event.citations && event.citations.length > 0) {
                    citations = event.citations;
                  }
                  // Актуальный баланс токенов после списания
                  if (event.newBalance !== undefined) {
                    newBalance = event.newBalance;
                  }
                }

            } catch (parseError) {
              // Re-throw intentional stream errors (stream.error event)
              // The catch was meant for JSON.parse failures only
              if (parseError instanceof Error && (parseError as any).errorType) {
                throw parseError;
              }
              // Otherwise it's a JSON parse error — ignore silently
            }
          }
        }
      }
      } finally {
        reader.releaseLock();
      }
      
      // Возвращаем метаданные после завершения streaming
      return {
        sources,
        searchQueries,
        userMessageId,
        assistantMessageId,
        usage,
        // ✅ Возвращаем версии сообщения ассистента (при редактировании)
        ...(assistantVersions && {
          assistantVersions,
          assistantCurrentVersionIndex,
          assistantEditSessionId
        }),
        newBalance,
        // ✅ Reasoning content (Extended Thinking)
        ...(reasoningContent && { reasoningContent }),
        // ✅ Citations (inline url_citation)
        ...(citations.length > 0 && { citations })
      };
      
    } catch (error: any) {
      // Обработка timeout ошибки
      if (error.name === 'AbortError') {
        const timeoutError = new Error(
          (isDeepResearch || isLongThinking)
            ? 'Превышено время ожидания (45 минут). Попробуйте упростить запрос или разбить на части.'
            : 'Превышено время ожидания ответа (15 минут). Попробуйте снова или используйте более простой запрос.'
        );
        (timeoutError as any).errorType = 'timeout_error';
        (timeoutError as any).statusCode = 408;
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ===== CHAT SESSIONS API =====
  
  async getChatSessions(includeArchived = false): Promise<any> {
    const response = await this.client.get(`/api/chat/sessions?includeArchived=${includeArchived}`);
    return response.data;
  }

  async getGeneratingMessages(sessionId: string): Promise<any> {
    const response = await this.client.get(`/api/chat/sessions/${sessionId}/generating`);
    return response.data;
  }

  async getArchivedChatSessions(): Promise<any> {
    const response = await this.client.get('/api/chat/sessions/archived');
    return response.data;
  }

  async createChatSession(name = 'New Chat', isSecret = false, password = ''): Promise<any> {
    const response = await this.client.post('/api/chat/sessions', { name, isSecret, password });
    return response.data;
  }

  async verifySecretChatPassword(sessionId: string, password: string): Promise<any> {
    const response = await this.client.post(`/api/chat/sessions/${sessionId}/verify`, { password });
    return response.data;
  }

  async renameChatSession(sessionId: string, name: string): Promise<any> {
    const response = await this.client.patch(`/api/chat/sessions/${sessionId}`, { name });
    return response.data;
  }

  async archiveChatSession(sessionId: string, archived = true): Promise<any> {
    const response = await this.client.patch(`/api/chat/sessions/${sessionId}/archive`, { archived });
    return response.data;
  }

  async deleteChatSession(sessionId: string): Promise<any> {
    const response = await this.client.delete(`/api/chat/sessions/${sessionId}`);
    return response.data;
  }

  async searchChatSessions(query: string): Promise<any> {
    const response = await this.client.get(`/api/chat/sessions/search?q=${encodeURIComponent(query)}`);
    return response.data;
  }

  async getChatSessionHistory(sessionId: string, limit = 100, aroundMessageId?: string): Promise<any> {
    let url = `/api/chat/sessions/${sessionId}/history?limit=${limit}`;
    if (aroundMessageId) url += `&around=${encodeURIComponent(aroundMessageId)}`;
    const response = await this.client.get(url);
    return response.data;
  }

  /**
   * Получить статусы генерирующихся сообщений в сессии
   * Используется для восстановления статуса после перезагрузки страницы
   */
  async getGeneratingStatus(sessionId: string): Promise<any> {
    const response = await this.client.get(`/api/chat/history/generating-status?sessionId=${sessionId}`);
    return response.data;
  }

  /**
   * Удалить конкретное сообщение из чата
   * @param messageId - ID сообщения для удаления
   */
  async deleteMessage(messageId: string): Promise<{ success: boolean; deletedMessageId?: string; error?: string }> {
    const response = await this.client.delete(`/api/chat/history/messages/${messageId}`);
    return response.data;
  }

  /**
   * Clear all chat history
   */
  async clearChatHistory(): Promise<any> {
    const response = await this.client.delete('/api/chat/history');
    return response.data;
  }

  /**
   * Update message versions
   */
  async updateVersions(data: { messageId: string; versions: any[] }): Promise<any> {
    const response = await this.client.patch('/api/chat/history/update-versions', data);
    return response.data;
  }

  // ===== CHAT FOLDERS (PROJECTS) =====

  async getChatFolders(): Promise<any> {
    const response = await this.client.get('/api/chat/folders');
    return response.data;
  }

  async createChatFolder(name: string, contextInstruction?: string): Promise<any> {
    const response = await this.client.post('/api/chat/folders', { name, contextInstruction });
    return response.data;
  }

  async getChatFolder(folderId: string): Promise<any> {
    const response = await this.client.get(`/api/chat/folders/${folderId}`);
    return response.data;
  }

  async renameChatFolder(folderId: string, name: string): Promise<any> {
    const response = await this.client.patch(`/api/chat/folders/${folderId}`, { name });
    return response.data;
  }

  async updateChatFolderInstruction(folderId: string, instruction: string): Promise<any> {
    const response = await this.client.patch(`/api/chat/folders/${folderId}/instruction`, { instruction });
    return response.data;
  }

  async deleteChatFolder(folderId: string): Promise<any> {
    const response = await this.client.delete(`/api/chat/folders/${folderId}`);
    return response.data;
  }

  // ===== PIN / MOVE =====

  async pinChatSession(sessionId: string, pinned: boolean): Promise<any> {
    const response = await this.client.patch(`/api/chat/sessions/${sessionId}/pin`, { pinned });
    return response.data;
  }

  async moveChatToFolder(sessionId: string, folderId: string | null): Promise<any> {
    const response = await this.client.patch(`/api/chat/sessions/${sessionId}/folder`, { folderId });
    return response.data;
  }
}

